import { Platform } from 'quasar'
import { userService } from 'src/common/user-service'
import { getFingerprint, getLocale } from 'src/common/functions'
import { TokenService } from 'src/common/token-service'
import { getConfig } from 'src/configs'

const tokenService = new TokenService()

const config = getConfig()

export class API {
  axios = void 0
  store = void 0
  isServer = process.env.SERVER

  constructor(axios, store) {
    this.store = store
    this.axios = axios

    if (Platform.is.mobile) {
      this.axios.defaults.headers.common['CASISMOBILE'] = 1
    }

    this.addRequestInterceptor(axios)
    this.addResponseInterceptor(axios)
  }

  addRequestInterceptor = (axios) => {
    axios.interceptors.request.use(
      async (config) => {
        let tokenFromCookies = tokenService.getToken()
        let tokenFromStore = this.store.getters.getToken
        const token = tokenFromStore || tokenFromCookies

        if (token) {
          config.headers.Authorization = `JWT ${token}`
        }

        config.headers['Accept-Language'] = this.store.getters.getUserLanguage || getLocale()
        config.headers['client-timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  addResponseInterceptor = (axios) => {
    axios.interceptors.response.use(
      (response) => {
        return response
      },
      async (error) => {
        const originalRequest = error.config

        if (originalRequest?.url === 'token-refresh/' || originalRequest._retry) {
          return Promise.reject(error)
        }

        const tokenIsExpired =
          (error.response?.status === 401 && error.config.url !== '/authservice/api/v1/user/signin/') ||
          (error.response?.data?.non_field_errors &&
            error.response?.data?.non_field_errors[0] === 'Signature has expired.')

        if (tokenIsExpired) {
          if (this.isServer) {
            this.store.dispatch('removeToken')
            console.error('Token was removed. API.js | 87')
            tokenService.removeToken()
          } else {
            console.error('Token was removed. API.js | 95')
            this.store.dispatch('removeToken')
            await userService.logout()
          }

          const ENDPOINTS_USER = [/signin\//, /signin\/bk\/.*/]

          if (ENDPOINTS_USER.some((rx) => rx.test(originalRequest?.url))) {
            return Promise.reject(error)
          }

          originalRequest._retry = true
          delete originalRequest.headers.Authorization
          return this.axios(originalRequest)
        }

        return Promise.reject(error)
      }
    )
  }

  // ------------------------------------------- COMMON ---------------------------------------------------------------
  common = {
    underDev: async () => {
      return this.axios.get('underdev/', {
        cache: {
          ignoreCache: true,
          readOnError: false,
        },
      })
    },
  }
  // ------------------------------------------- CASHBOX --------------------------------------------------------------
  cashbox = {
    getTopupBankMethods: async () => this.axios.get('bank/get/topup/methods/'),
    getTerminalToPup: async (params) => this.axios.post('bank/get/topup/terminal/', params),
    getWithdrawBankMethods: async () => this.axios.get('bank/get/withdraw/methods/'),
    getWithdrawTerminal: async (data) => this.axios.post('bank/get/withdraw/terminal/', data),
    getSmsToTransferToBK: async () => this.axios.get('bank/bk/withdraw/sms/'),
    bankCheck: async (params) => this.axios.post('bank/bk/check/', params),

    bankBKWithdraw: async (params) => this.axios.post('bank/bk/withdraw/', params),
    bankBKTopup: async (params) => this.axios.post('bank/bk/topup/', params),
  }
  // ------------------------------------------- PROFILE --------------------------------------------------------------
  profile = {
    // --- Messages ---
    messagesList: async () => this.axios.get('user/messages/list/'),

    getCodeForSmsVerification: async () => this.axios.get('bank/bk/disable/sms/'),
    approveDisableSmsVerification: async (params) => this.axios.post('bank/bk/disable/sms/approve/', { params }),
    approveEnableSmsVerification: async () => this.axios.get('bank/bk/enable/sms/approve/'),
  }
  // ------------------------------------------- COMPETITION ----------------------------------------------------------
  competitions = {
    loadmore: async (url) => {
      return this.axios.get(`${url}`)
    },
  }
  // ------------------------------------------- BONUSES --------------------------------------------------------------
  bonuses = {
    chooseWelcomeBonus: async (payload) => {
      return this.axios.post('user/bonus/welcome/add/', payload)
    },
    getBonus: async (id) => {
      return this.axios.get(`bonus/${id}/info/`)
    },
    activateBonus: async (payload) => this.axios.post('user/bonus/activate/', payload),
  }
  // ------------------------------------------- AUTH -----------------------------------------------------------------
  auth = {
    signIn: async (data) => {
      const fingerPrint = await getFingerprint()

      return this.axios({
        url: '/authservice/api/v1/user/signin/',
        baseURL: '',
        data,
        method: 'post',
        headers: {
          Fingerprint: fingerPrint,
        },
      })
    },
    refreshToken: async (payload) => {
      return this.axios.post('token-refresh/', payload, {
        headers: {
          Authorization: null,
        },
      })
    },
    // getSmsForRecoveryPass: async (payload) => {
    //   return this.axios.post('user/password/reset/getsmscode/', payload)
    // },
    sendPhoneToGetSms: async (data) => {
      return this.axios({
        url: '/authservice/api/v1/user/reset-password/sms/send-code/',
        method: 'post',
        data,
        baseURL: '',
      })
    },
    checkSMScode: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/reset-password/sms/check-code/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    sendCode: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/sms/send-code/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    checkCode: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/sms/check-code/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    setPassword: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/set-password',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    socialRegistration: async (slug, payload) => {
      return this.axios({
        url: `/authservice/api/v1/user/${slug}/registration/`,
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    socialProvidersList: async () => {
      return this.axios({
        url: '/authservice/api/v1/socialnetwork/provider/list/',
        baseURL: '',
      })
    },
    registration: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/pre-registration/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    finallyRegistration: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/finally-registration/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
    registrationOneStep: async (payload) => {
      const fingerPrint = await getFingerprint()
      return this.axios({
        url: '/authservice/api/v1/user/registration/one-step/',
        method: 'post',
        data: payload,
        baseURL: '',
        headers: {
          Fingerprint: fingerPrint,
        },
      })
    },

    getSession: async () => {
      return this.axios({
        url: '/casino/api/g/auth/session/',
        method: 'get',
        baseURL: '',
      })
    },
    setSession: async (data) => {
      return this.axios({
        url: '/casino/api/g/auth/session/bk/',
        method: 'post',
        baseURL: '',
        data,
      })
    },
    removeSession: async () => {
      return this.axios({
        url: '/casino/api/g/auth/session/',
        method: 'delete',
        baseURL: '',
      })
    },
    twoAuth: async (payload) => {
      return this.axios({
        url: '/authservice/api/v1/user/2fa/check_code/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
  }

  parse = {
    sportbook: async (locale = 'en') => {
      return this.axios({
        url: '/parse',
        baseURL: window.location.origin,
        method: 'post',
        timeout: 60 * 1000,
        data: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          locale,
          platform: process.env.PLATFORM,
        },
      })
    },
  }

  payments = {
    list: {
      topup: async (payload, options = {}) => {
        return this.axios.get('/paygate/method/topup/list/', payload, options)
      },
      withdraw: async (payload, options = {}) => {
        return this.axios.get('/paygate/method/withdraw/list/', payload, options)
      },
    },
    create: {
      topup: (payload, config = {}) => this.axios.post('/paygate/order/topup/create/', payload, config),
      withdraw: (payload, config = {}) => this.axios.post('/paygate/order/withdraw/create/', payload, config),
    },
    form: {
      topup: (payload) => this.axios.get('/paygate/order/topup/init/', payload),
      withdraw: (payload) => this.axios.get('/paygate/order/withdraw/init/', payload),
    },
    sms: {
      channels: (payload) => this.axios.get('/paygate/order/withdraw/channels/', payload),
      getCode: (payload) => this.axios.post('/paygate/order/withdraw/code/', payload),
    },
    init: {
      topup: (payload) => this.axios.post('/paygate/order/topup/init/', payload),
      next: (payload) => this.axios.post('/paygate/order/topup/next/', payload),
      withdraw: (payload) => this.axios.post('/paygate/order/withdraw/init/', payload),
      getUrl: (payload) => this.axios.get('/paygate/order/topup/url/', payload),
    },
    history: {
      all: (payload) => this.axios.get(config.api.transactions, payload),
      topup: (payload) => this.axios.get('/paygate/order/topup/list/', payload),
      withdraw: (payload) => this.axios.get('/paygate/order/withdraw/list/', payload),
      refund: (payload) => this.axios.post('/paygate/order/withdraw/refund/', payload),
    },
    code: {
      channels: (payload) => this.axios.get('/paygate/code/channels/', payload),
      get: (payload) => this.axios.post('/paygate/code/get/', payload),
      disable: (payload) => this.axios.post('/paygate/code/disable/', payload),
      enable: (payload) => this.axios.post('/paygate/code/enable/', payload),
    },
  }

  statistic = {
    send: async (payload) => {
      return this.axios({
        url: '/api_dwh/v1/provider1/',
        method: 'post',
        data: payload,
        baseURL: '',
      })
    },
  }

  dwh = {
    sendEvent: (eventName, region = '', device = 'UNDEFINED', params = {}) => {
      return this.axios({
        url: '/dwh-proxy/',
        method: 'post',
        data: {
          events: [
            {
              timestamp: Date.now(),
              geo: region,
              eventName: eventName,
              ...params,
            },
          ],
          dataSource: {
            platform: 'CASINO',
            origin: device,
          },
        },
      })
    },
  }
}
